import styled from "styled-components";

interface FigureProps {
  $backgroundColor?: string;
  $backgroundPosition: string;
  $backgroundRepeat: string;
  $backgroundSize: string;
  $center: boolean;
  $desktopBackgroundUrl?: string;
  $isCard: boolean;
  $isInPLPGrid: boolean;
  $layout: string;
  $mobileBackgroundUrl?: string;
  $theme: string;
  $useTransparentBackground: boolean;
}
export const Figure = styled.figure<FigureProps>`
  margin: 0;
  display: flex;

  ${({ $isCard }) => $isCard && `
    overflow: hidden;
    border-radius: 16px;
  `}

  ${({ $layout }) => {
    switch ($layout) {
      case 'column-image-above':
        return `
          flex-direction: column;
        `;

      case 'column-image-below':
        return `
          flex-direction: column-reverse;
        `;
      case 'row-image-right':
      case 'row-image-left':
      case 'wide-image-right':
      case 'wide-image-left':
        return `
          flex-direction: column;

          @media (min-width: 768px) {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
        `;
      default:
        return ``;
    }
  }}

  ${({ $layout }) => $layout.includes('skinny-banner') && `
    flex-direction: column;
    padding-top: 80px;
    padding-bottom: 80px;

    @media (min-width: 1024px) {
      padding-top: 66px;
      padding-bottom: 66px;
    }
  `}

  ${({ $center }) => $center && `
    text-align: center;
    align-items: center;
    justify-content: center;
  `}

  ${({ $theme }) => $theme == 'dark' ? `
    color: #fff;
    background-color: #000;

    p, h1, h2, h3, h4, h5 {
      color: #fff;
    }
  ` : `
    color: #000;
  `}

  ${({ $backgroundColor }) => $backgroundColor && `
    background-color: #${$backgroundColor};
  `}

  ${({ $useTransparentBackground }) => $useTransparentBackground && `
    background-color: transparent;
  `}

  hr {
    height: 2px;
    margin: 8px 0;
    border: 1px solid #4c4d4d;
    background-color: transparent;
  }

  ${({ $desktopBackgroundUrl, $backgroundPosition, $backgroundSize, $backgroundRepeat }) => $desktopBackgroundUrl && `
    background-image: url(${$desktopBackgroundUrl});
    background-repeat: ${$backgroundRepeat};
    background-position: ${$backgroundPosition};
    background-size: ${$backgroundSize};
  `}

  ${({ $mobileBackgroundUrl, $backgroundPosition, $backgroundSize, $backgroundRepeat }) => $mobileBackgroundUrl && `
    @media screen and (max-width: 768px) {
      background-image: url(${$mobileBackgroundUrl});
      background-repeat: ${$backgroundRepeat};
      background-position: ${$backgroundPosition};
      background-size: ${$backgroundSize};
    }
  `}

  ${({ $isInPLPGrid }) => $isInPLPGrid && `
    position: absolute;
    width: 100%;
    height: 100%;
    justify-content: flex-end;

    figcaption {
      margin: 16px;
    }
  `}

  ${({ $isInPLPGrid, $center }) => $isInPLPGrid && $center && `
    figcaption {
      position: absolute;
      margin: 0;
    }
  `}
`;

export const Picture = styled.picture<{ $layout: string; }>`
  display: flex;

  // Height will be set dynamically, see setPictureHeight()
  ${({ $layout }) => $layout.includes('wide') && `
    @media (min-width: 768px) {
      flex: 0 0 65%;
      height: 0;
      width: 100%;
      overflow: hidden;

      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
      }
    }
  `}

  ${({ $layout }) => ($layout == 'wide-image-right' || $layout == 'row-image-right') && `
    @media (min-width: 768px) {
      order: 1;
    }
  `}

  ${({ $layout }) => ($layout == 'row-image-right' || $layout == 'row-image-left') && `
    @media (min-width: 768px) {
      flex: 0 0 calc(50% - 16px);
    }
  `}

  ${({ $layout }) => $layout == 'skinny-banner' && `
    padding: 0;
    margin-left: 0;
    margin-right: auto;
  `}
`;

export const Image = styled.img<{ $maxWidth?: number; $hide?: string; }>`
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth ? `${$maxWidth}px` : '100%'};

  ${({ $hide }) => $hide == 'small' && `
    display: none;

    @media (min-width: 768px) {
      display: block;
    }
  `}

  ${({ $hide }) => $hide == 'large' && `
    @media (min-width: 768px) {
      display: none;
    }
  `}
`;

interface CaptionProps {
  $brand: string;
  $layout: string;
  $maxWidth?: number;
  $noPadding: boolean;
  $textPadding: string;
  carouselStyle: string;
}

export const Caption = styled.figcaption<CaptionProps>`
  margin: auto;
  max-width: ${({ $maxWidth }) => $maxWidth ? `${$maxWidth}px` : '100%'};
  font-size: 1rem;

  ${({ $textPadding }) => $textPadding == 'Large' && `
    padding: 2rem 3rem;
    @media (min-width: 768px) {
      padding: 2rem 4rem;
    }
  `}
  ${({ $textPadding }) => $textPadding == 'Small' && `
    padding: 1rem;
  `}
  ${({ $textPadding }) => $textPadding == 'None' && `
    padding: 0;
  `}
  /*
    This will eventually be deprecated in favor of $textPadding = 'None',
    but until then will override the other options.
  */
  ${({ $noPadding }) => $noPadding && `
    padding: 0;
  `}

  ${({ $layout }) => ($layout == 'row-image-right' || $layout == 'row-image-left') && `
    // this is needed to get full-width banners to align correctly - not sure if there are negative effects elsewhere.
    margin-left: 0;

    @media (min-width: 768px) {
      flex: 0 0 calc(50% - 16px);
    }
  `}

  ${({ $layout, $brand }) => $layout == 'skinny-banner' && `
    margin-top: 16px;
    margin-left: 0;
    margin-right: auto;
    padding: 0;

    div:first-of-type {
      max-width: 400px;
    }

    h4, p {
      margin: 0;
    }

    ${$brand == 'fender' && `
      h1, h2, h3, h4 {
        font-size: 24px;
        line-height: 24px;
        letter-spacing: -0.48px;
        font-weight: 500;
      }

      p {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.14px;
        margin-top: 8px;
      }
    `}

    @media (min-width: 768px) {
      display: flex;
      justify-content: space-between;
      margin: 0;
      padding: 0;
      padding-top: 24px;
      align-items: center;
    }
  `}

  ${({ carouselStyle }) => carouselStyle == 'Product Highlight' && `
    p {
      font-size: 14px;
      line-height: 22px;
    }
  `}
`;

export const ButtonContainer = styled.div<{ $center: boolean; $layout: string; $isInPLPGrid: boolean; }>`
  display: flex;
  flex-direction: column;
  margin: 30px auto 0;

  @media (min-width: 768px) {
    flex-direction: row;
  }

  ${({ $center }) => $center && `
    justify-content: center;
  `}

  > a:nth-of-type(2) {
    margin-top: 16px;

    @media (min-width: 768px) {
      margin-top: 0px;
      margin-left: 32px;
    }
  }

  ${({ $layout }) => $layout == 'skinny-banner' && `
    margin: 0;
    margin-top: 16px;
    width: fit-content;
    display: block;

    a {
      padding: 10px 24px;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0.16px;
    }

    @media (min-width: 768px) {
      margin-top: 0;

      a {
        padding: 17px 50px;
      }
    }
  `}

  ${({ $isInPLPGrid }) => $isInPLPGrid && `
    margin: 16px auto 0px;
  `}
`;

export const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 0px 0px;

  .custom-fraction-pagination {
    font-family: "HelveticaNowDisplay";
    font-size: 14px;
    z-index: 10;
    font-weight: 500;
    line-height: 18px;
    display: flex;
    gap: 4px;
  }

  .custom-product-highlight-navigation {
    display: none;

    @media (min-width: 768px) {
      display: block;
    }
  }
`;

export const FenderSkinnyContainer = styled.div<{ $center: boolean; }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

export const FenderSkinnyLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FenderSkinnyRight = styled.div`
  display: flex;

  @media (min-width: 768px) {
    align-items: center;
  }
`;
